import {
  Combobox,
  DropdownButton,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import { FiPlus } from 'react-icons/fi'
import { GetComposerSutsRequest } from '@eggplant/api-requests'
import { HiOutlineDesktopComputer } from 'react-icons/hi'
import {
  LinkToAddSut,
  useSpaceAccess
} from '@eggplant/routing'
import type {
  ButtonShape,
  DropdownPlacement,
  Options,
  SutResponse
} from '@eggplant/types'
import type { options } from '@eggplant/pattern-library/src/lib/components/types'

interface Props {
  availableSuts?: SutResponse[]
  isDisabled?: boolean
  isFilter?: boolean
  isFilterFixedMinMaxWidth?: boolean
  isRequired?: boolean
  dropdownPlacement?: DropdownPlacement
  onChange: (value: string) => void
  placeholder?: string
  value?: string
}

const getOptions = (items: SutResponse[] | undefined) => {
  if (!Array.isArray(items)) return []

  return items.map(item => ({
    label: item.name,
    value: item.id
  }))
}

const SelectSUTFilter = ({
  availableSuts,
  isDisabled = false,
  isFilter = false,
  isFilterFixedMinMaxWidth = true,
  isRequired = false,
  dropdownPlacement = 'bottom',
  onChange,
  placeholder = 'SUT',
  value
// eslint-disable-next-line complexity
}: Props) => {
  const [isAccessible] = useSpaceAccess()

  const renderButton = (options: Options) => {
    const currentOption = options.find(item => item.value === value)

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const tooltip = currentOption?.value
      ? `SUT: ${currentOption.label}`
      : label

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={HiOutlineDesktopComputer}
        dropdownPlacement={dropdownPlacement}
        isDisabled={isDisabled}
        isFilter={isFilter}
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        isHighlighted={!!value}
        label={label}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={<HiOutlineDesktopComputer size='24px' />}
      linkTo={
        isAccessible
          ? (
            <LinkToAddSut>
              <FiPlus
                aria-hidden
                size={16}
              />
              New SUT
            </LinkToAddSut>
          )
          : undefined
      }
      title='No SUT found'
    />

  const renderInput = (options: options) =>
    <Combobox
      dropdownPlacement={dropdownPlacement}
      isDisabled={isDisabled}
      isFilter={isFilter}
      isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
      isRequired={isRequired}
      noOptionsPlaceholder={noDataPlaceholder}
      onChange={onChange}
      options={options}
      renderButton={renderButton(options)}
      value={value}
    />

  if (availableSuts) {
    return renderInput(getOptions(availableSuts))
  }

  return (
    <GetComposerSutsRequest shortErrorMessage='Failed to load SUT'>
      {({ items }) => renderInput(getOptions(items))}
    </GetComposerSutsRequest>
  )
}

export default SelectSUTFilter
